import clsx from 'clsx';
import type { DraggableAttributes, DraggableSyntheticListeners } from '@dnd-kit/core';
import type { POI } from '@poi-planner/shared';
import { formatTimeBudget, formatDistance } from '../utils/format';

interface SidebarPoiCardProps {
  poi: POI;
  // Visual states
  isSelected?: boolean;
  isTargeted?: boolean;
  isDragging?: boolean;
  // Badges
  scheduledCount?: number;
  distance?: number;
  showDistance?: boolean;
  // Pin state (trip context)
  isPinned?: boolean;
  isScheduled?: boolean;
  onTogglePin?: (poiId: string) => void;
  onShowPinMessage?: (message: string) => void;
  // Callbacks
  onClick?: () => void;
  onEdit?: () => void;
  onSetTarget?: (poiId: string | null) => void;
  // DnD support (forwarded from wrapper)
  dndRef?: (node: HTMLElement | null) => void;
  dndAttributes?: DraggableAttributes;
  dndListeners?: DraggableSyntheticListeners;
}

export function SidebarPoiCard({
  poi,
  isSelected = false,
  isTargeted = false,
  isDragging = false,
  scheduledCount = 0,
  distance,
  showDistance = false,
  isPinned = false,
  isScheduled = false,
  onTogglePin,
  onShowPinMessage,
  onClick,
  onEdit,
  onSetTarget,
  dndRef,
  dndAttributes,
  dndListeners,
}: SidebarPoiCardProps) {
  const hasCoords = poi.lat != null && poi.lng != null;
  const isDraggable = !!dndListeners;

  return (
    <div
      ref={dndRef}
      data-poi-id={poi.id}
      onClick={onClick}
      {...(dndListeners ?? {})}
      {...(dndAttributes ?? {})}
      className={clsx(
        'group p-3 rounded-xl border cursor-pointer transition-all',
        'hover:shadow-md',
        isDragging && 'opacity-40',
        isTargeted
          ? 'border-blue-500 bg-blue-50 shadow-[0_0_0_3px_rgba(59,130,246,0.15)]'
          : isSelected
            ? 'border-blue-500 bg-blue-50 shadow-sm'
            : 'border-border bg-white hover:border-slate-300'
      )}
    >
      <div className="flex justify-between items-start gap-2">
        <div className="flex-1 min-w-0">
          <span className={clsx(
            'text-sm font-bold block truncate',
            isSelected || isTargeted ? 'text-blue-700' : 'text-text-primary'
          )}>
            {poi.name}
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {/* Distance badge */}
          {showDistance && (
            distance !== undefined ? (
              <span className="text-[10px] font-bold px-1.5 py-px rounded bg-blue-500/10 text-blue-500">
                {formatDistance(distance)}
              </span>
            ) : (
              <span className="text-[10px] font-semibold px-1.5 py-px rounded bg-input-bg text-gray-400 italic">
                No location
              </span>
            )
          )}
          {/* No location badge (when not showing distance) */}
          {!showDistance && !hasCoords && (
            <span
              className="text-[10px] font-semibold px-1.5 py-px rounded bg-amber-100 text-amber-700"
              title="No coordinates"
            >
              No location
            </span>
          )}
          {/* Scheduled count badge */}
          {scheduledCount > 0 && (
            <span className="text-[10px] font-bold px-1.5 py-px rounded bg-blue-100 text-blue-800">
              {scheduledCount}x
            </span>
          )}
          {/* Pin button (trip context only) */}
          {onTogglePin && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (isScheduled) {
                  onShowPinMessage?.('Remove from day first before unpinning');
                } else {
                  onTogglePin(poi.id);
                }
              }}
              title={
                isPinned && isScheduled
                  ? 'Remove from day first before unpinning'
                  : isPinned
                    ? 'Unpin from trip'
                    : 'Pin to trip'
              }
              className={clsx(
                'flex items-center justify-center size-6 rounded-md transition-all',
                isPinned
                  ? 'text-purple-600 opacity-100'
                  : 'text-text-secondary opacity-0 group-hover:opacity-100 hover:bg-purple-500/10 hover:text-purple-500'
              )}
            >
              <span className="material-symbols-outlined text-base">
                {isPinned ? 'keep' : 'keep_off'}
              </span>
            </button>
          )}
          {/* Target button */}
          {hasCoords && onSetTarget && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSetTarget(isTargeted ? null : poi.id);
              }}
              title={isTargeted ? 'Clear proximity target' : 'Sort by distance from this POI'}
              className={clsx(
                'flex items-center justify-center size-6 rounded-md transition-all',
                isTargeted
                  ? 'bg-blue-500 text-white'
                  : 'text-text-secondary opacity-0 group-hover:opacity-100 hover:bg-blue-500/10 hover:text-blue-500'
              )}
            >
              <span className="material-symbols-outlined text-base">gps_fixed</span>
            </button>
          )}
          {/* Edit button */}
          {onEdit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className={clsx(
                'flex items-center justify-center size-6 rounded-md transition-all',
                isSelected || isTargeted
                  ? 'hover:bg-blue-200 text-blue-600'
                  : 'text-text-secondary opacity-0 group-hover:opacity-100 hover:bg-slate-100'
              )}
              title="Edit POI"
            >
              <span className="material-symbols-outlined text-base">edit</span>
            </button>
          )}
          {/* Drag indicator */}
          {isDraggable && (
            <span className="material-symbols-outlined text-gray-300 text-lg">
              drag_indicator
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1.5 text-text-secondary text-xs mt-1">
        <span className="material-symbols-outlined text-sm">schedule</span>
        <span>{formatTimeBudget(poi.timeBudget)}</span>
      </div>

      {poi.tags?.length > 0 && (
        <div className="flex gap-1 flex-wrap mt-2">
          {poi.tags?.map((tag) => (
            <span
              key={tag}
              className={clsx(
                'text-[10px] py-0.5 px-1.5 rounded-md font-medium',
                isSelected || isTargeted
                  ? 'bg-blue-200 text-blue-700'
                  : 'bg-slate-100 text-text-secondary'
              )}
            >
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
